import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Select, Switch } from "antd"
import { DeleteOutlined } from "@ant-design/icons"
import {
  CButton,
  CModal,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react"
import dayjs from "dayjs"
import AdminTable from "../Helpers/AdminTable"
import {
  cancelLockerRent,
  getLockerRents,
} from "src/store/Slices/Administrator/LockersSlice"
import LockerRentDetailsModal from "./Modals/LockerRentDetailsModal"

const FALLBACK_COLUMNS = {
  data: {
    value: {
      columns: [
        { title: "№", dataIndex: "id" },
        { title: "Шкафчик", dataIndex: "lockerId" },
        { title: "Номер", dataIndex: "lockerNumber" },
        { title: "Клуб", dataIndex: "clubName" },
        { title: "Клиент", dataIndex: "userId" },
        { title: "Телефон", dataIndex: "userPhone" },
        { title: "Начало", dataIndex: "startDate" },
        { title: "Окончание", dataIndex: "endDate" },
        { title: "Статус", dataIndex: "status" },
        { title: "Цена", dataIndex: "price" },
      ],
    },
  },
}

const formatDate = (value) =>
  value ? dayjs(value).format("DD.MM.YYYY HH:mm") : ""

const LockerRents = () => {
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [isDeleted, setIsDeleted] = useState(false)
  const [isReleased, setIsReleased] = useState()
  const [tableFilters, setTableFilters] = useState({})
  const [rentInfo, setRentInfo] = useState()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)

  const list = useSelector((state) => state.LockersSlice.lockerRentsList)
  const loading = useSelector(
    (state) => state.LockersSlice.lockerRentsListLoading,
  )

  const load = (
    nextPage = page,
    nextPageSize = pageSize,
    filters = tableFilters,
    deleted = isDeleted,
    released = isReleased,
  ) => {
    dispatch(
      getLockerRents({
        isDeleted: deleted,
        count: nextPageSize,
        offset: nextPageSize * (nextPage - 1),
        ...(released != undefined ? { isReleased: released } : {}),
        ...filters,
      }),
    )
  }

  useEffect(() => {
    load(1, 20, {}, false, undefined)
  }, [])

  const searchItemsMap = (keys, values) => {
    const formattedItems = {}
    if (!keys || !values) return formattedItems
    keys.forEach((key, index) => {
      const selected = values[index]
      if (selected == undefined) return
      if (key === "id") {
        if (selected[0] !== undefined && selected[0] !== "") {
          formattedItems.idFrom = selected[0]
        }
        if (selected[1] !== undefined && selected[1] !== "") {
          formattedItems.idTo = selected[1]
        }
      } else if (key === "clubName" || key === "clubId" || key === "club") {
        if (selected[0] != undefined) formattedItems.clubId = selected[0]
      } else if (key === "lockerId" || key === "userId" || key === "userPhone") {
        if (selected[0] != undefined && selected[0] !== "") {
          formattedItems[key] = selected[0]
        }
      }
    })
    return formattedItems
  }

  const handleTableChange = (pagination, filters, sorter) => {
    const nextFilters = {
      ...searchItemsMap(Object.keys(filters), Object.values(filters)),
      orderBy: sorter?.column?.dataIndex,
      isDesc: sorter?.order ? sorter.order === "ascend" : undefined,
    }
    setTableFilters(nextFilters)
    setPage(pagination.current)
    setPageSize(pagination.pageSize)
    load(pagination.current, pagination.pageSize, nextFilters)
  }

  const showDeletedItems = (value) => {
    setIsDeleted(value)
    setPage(1)
    load(1, pageSize, tableFilters, value)
  }

  const onReleasedChange = (value) => {
    setIsReleased(value)
    setPage(1)
    load(1, pageSize, tableFilters, isDeleted, value)
  }

  const onCancel = () => {
    if (!rentInfo?.id) return
    dispatch(cancelLockerRent(rentInfo.id)).then((result) => {
      const payload = result.payload
      if (payload?.response || payload?.data?.isSuccess === false) return
      setCancelOpen(false)
      setPage(1)
      load(1, pageSize, tableFilters)
    })
  }

  const rows = list?.data?.value?.rows?.map((row) => ({
    ...row,
    startDate: formatDate(row.startDate),
    endDate: formatDate(row.endDate),
  }))

  const items = [
    {
      key: "details",
      label: "Детали",
      onClick: () => setDetailsOpen(true),
    },
    {
      key: "cancel",
      label: "Отменить аренду",
      onClick: () => setCancelOpen(true),
    },
  ]

  return (
    <>
      <LockerRentDetailsModal
        visible={detailsOpen}
        rentId={rentInfo?.id}
        onClose={() => setDetailsOpen(false)}
      />
      <CModal
        alignment='center'
        visible={cancelOpen}
        onClose={() => setCancelOpen(false)}
        size='sm'
      >
        <CModalHeader>
          <CModalTitle>Отменить аренду?</CModalTitle>
        </CModalHeader>
        <CModalFooter
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 12,
            borderTop: "unset",
          }}
        >
          <div>Шкафчик будет открыт.</div>
          <div style={{ display: "flex", justifyContent: "space-evenly" }}>
            <CButton
              onClick={onCancel}
              style={{ color: "#fff", padding: "3px 50px" }}
              color='primary'
            >
              Да
            </CButton>
            <CButton
              style={{ color: "#fff", padding: "3px 50px" }}
              color='secondary'
              onClick={() => setCancelOpen(false)}
            >
              Нет
            </CButton>
          </div>
        </CModalFooter>
      </CModal>
      <AdminTable
        scroll={{ x: 1400, y: 600 }}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onChange={handleTableChange}
        loading={loading}
        onClickRowOne={setRentInfo}
        columns={
          list?.data?.value?.columns?.length ? list : FALLBACK_COLUMNS
        }
        rows={rows}
        total={list?.data?.value?.totalCount}
        items={items}
        isDeleted={isDeleted}
        filterMode='lockerRents'
        title={() => (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 16,
              minHeight: 60,
              padding: "0 16px",
              background: "#fff",
            }}
          >
            <Select
              allowClear
              placeholder='Сдана'
              style={{ width: 160 }}
              value={isReleased}
              options={[
                { value: false, label: "Не сдана" },
                { value: true, label: "Сдана" },
              ]}
              onChange={onReleasedChange}
            />
            <Switch
              checkedChildren={<DeleteOutlined />}
              unCheckedChildren={<DeleteOutlined />}
              onChange={showDeletedItems}
              style={{ transform: "scale(1.1)" }}
            />
          </div>
        )}
      />
    </>
  )
}

export default LockerRents
